import { Mui } from "@osu/react-ui";
import { useState } from 'react';
import { useOverrideWorkflowDecisionMutation, useGetProgramResolveOptionsQuery, useGetAdminOverrideReasonsQuery } from '../../services/graphql/api';
import { skipToken } from "@reduxjs/toolkit/query";
import DialogSummaryBox from "../../Common/components/DialogSummaryBox";

const DialogAdminOverrideForAdvancedSearch = ({ openDialog, setOpenDialog, searchParams, clearSearchCriteria, clearGrid, skip, totalCount }) => {
    const [showSuccess, setShowSuccess] = useState(false);
    const [recommendation, setRecommendation] = useState("");
    const [selectedReason, setSelectedReason] = useState("");
    const [overrideWorkflowDecision, { isLoading: isSubmitting }] = useOverrideWorkflowDecisionMutation();
    const { data: recommendationOptions, loading: optionsLoading } = useGetProgramResolveOptionsQuery(skip ? skipToken : undefined);
    const { data: overrideReasons, loading: reasonsLoading } = useGetAdminOverrideReasonsQuery(skip ? skipToken : undefined);

    if (skip) { return; }
    const dialogMessage = `Choose an administrative recommendation (optional) and reason for the selected population:`;

    async function confirmAction(e) {
        e.preventDefault();
        e.stopPropagation();

        if (isSubmitting) return;

        try {
            const input = {
                criteria: searchParams,
                recommendation,
                overrideReason: selectedReason,
            };

            const result = await overrideWorkflowDecision({ input });

            if (result.data?.status === "success") {
                // Show success message
                setShowSuccess(true);
            } else {
                console.error("Administrative recommendation failed:", result);
                // TODO: Show error message to user
            }
        } catch (error) {
            console.error("Error submitting administrative recommendation:", error);
            // TODO: Show error message to user
        }
    }

    const handleClose = (cancel) => {
        if (cancel === false) {
            if (clearSearchCriteria) clearSearchCriteria();
            if (clearGrid) clearGrid();
        }
        setOpenDialog(false);
        setShowSuccess(false);
        setRecommendation("");
        setSelectedReason("");
    };

    // Prevent dialog from closing when showing success
    const handleDialogClose = (event, reason) => {
        if (showSuccess && reason === 'backdropClick') {
            return; // Prevent closing on backdrop click when showing success
        }
        if (!showSuccess) {
            handleClose();
        }
    };

    return (
        <Mui.Dialog
            open={openDialog}
            onClose={handleDialogClose}
            aria-labelledby="admin-override-dialog-title"
            aria-describedby="admin-override-dialog-description"
            fullWidth
            maxWidth="sm">
            <Mui.DialogTitle id="admin-override-dialog-title">Administrative Recommendation</Mui.DialogTitle>

            {showSuccess && (
                <Mui.Alert severity="info" sx={{ margin: 2, marginBottom: 0 }}>
                    Administrative recommendation initiated successfully!
                </Mui.Alert>
            )}

            <Mui.DialogContent>
                {!showSuccess && (
                    <Mui.DialogContentText id="admin-override-dialog-description">
                        {dialogMessage}
                    </Mui.DialogContentText>
                )}

                {!showSuccess && ((optionsLoading || reasonsLoading) ? (
                    <Mui.Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                        <Mui.CircularProgress />
                    </Mui.Box>
                ) : (
                    <Mui.Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Mui.FormControl sx={{ marginTop: "1rem" }} fullWidth>
                            <Mui.InputLabel id="admin-override-select-label">Recommendation</Mui.InputLabel>
                            <Mui.Select
                                labelId="admin-override-select-label"
                                id="admin-override-select"
                                value={recommendation}
                                onChange={(e) => setRecommendation(e.target.value)}
                                label="Recommendation"
                            >
                                <Mui.MenuItem value=""> - Choose a Recommendation - </Mui.MenuItem>
                                {(recommendationOptions || [])?.map((option) => (
                                    <Mui.MenuItem key={option.key} value={option.value}>
                                        {option.value}
                                    </Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                        <Mui.FormControl sx={{ marginTop: "1rem" }} fullWidth required>
                            <Mui.InputLabel id="reason-select-label">Reason</Mui.InputLabel>
                            <Mui.Select
                                labelId="reason-select-label"
                                id="reason-select"
                                value={selectedReason}
                                onChange={(e) => setSelectedReason(e.target.value)}
                                label="Reason"
                            >
                                <Mui.MenuItem value=""> - Choose a Reason - </Mui.MenuItem>
                                {(overrideReasons || [])?.map((option) => (
                                    <Mui.MenuItem key={option} value={option}>
                                        {option}
                                    </Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                        <DialogSummaryBox totalCount={totalCount}/>
                    </Mui.Box>
                ))}
            </Mui.DialogContent>

            <Mui.DialogActions className="padding-2">
                {showSuccess ? (
                    <Mui.Button color="primary" style={{ border: "none" }} variant="contained" onClick={handleClose}>
                        Close
                    </Mui.Button>
                ) : (
                    <>
                        <Mui.Button
                            color="secondary"
                            style={{ border: "none" }}
                            variant="outlined"
                            onClick={() => handleClose(true)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Mui.Button>
                        <Mui.Button
                            disabled={isSubmitting || optionsLoading || !selectedReason}
                            color="primary"
                            style={{ border: "none" }}
                            variant="contained"
                            onClick={confirmAction}
                        >
                            {isSubmitting && <Mui.CircularProgress size={20} style={{ marginRight: '.5rem' }} />}
                            Submit
                        </Mui.Button>
                    </>
                )}
            </Mui.DialogActions>
        </Mui.Dialog>
    );
};

export default DialogAdminOverrideForAdvancedSearch;
